/** Waste breakdown tab - by site, region, category, product, supplier, meal period, day &
 * time (heatmap), preparation area, and value/weight/volume/units views. */

import { Box, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../components/ui/Section';
import { applyRoundAndFormat } from '../../../utils';
import { CATEGORY_WASTE, DAY_HOUR_WASTE, MEAL_PERIOD_WASTE, PREP_AREA_WASTE, PRODUCT_WASTE, SITE_WASTE, SUPPLIER_WASTE } from '../data';
import { SiteWasteRow } from '../types';
import { PerformanceBandChip } from './StatusChip';

interface BreakdownTabProps {
  sites: SiteWasteRow[];
  currencyCode?: string;
}

const PIE_COLORS = ['#F04438', '#F79009', '#12B76A', '#2E90FA', '#7A5AF8', '#EE46BC', '#667085', '#B54708'];

const heatCellColor = (value: number, max: number, theme: Theme) => {
  const intensity = Math.min(1, value / max);
  if (intensity > 0.75) return theme.palette.error.light;
  if (intensity > 0.5) return theme.palette.warning.light;
  if (intensity > 0.25) return theme.palette.warning.light + '80';
  return theme.palette.success.light + '60';
};

const BreakdownTab = ({ sites, currencyCode }: BreakdownTabProps) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const days = Array.from(new Set(DAY_HOUR_WASTE.map((c) => c.day)));
  const hours = Array.from(new Set(DAY_HOUR_WASTE.map((c) => c.hour)));
  const maxHeat = Math.max(...DAY_HOUR_WASTE.map((c) => c.wasteCost));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardBySite', 'Waste by site')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('wasteDashboardSite', 'Site')}</TableCell>
                <TableCell>{t('wasteDashboardRegion', 'Region')}</TableCell>
                <TableCell align="right">{t('wasteDashboardWasteCost', 'Waste cost')}</TableCell>
                <TableCell align="right">{t('wasteDashboardWasteQuantity', 'Waste quantity (kg)')}</TableCell>
                <TableCell align="right">{t('wasteDashboardPercentOfSales', '% of sales')}</TableCell>
                <TableCell>{t('wasteDashboardPerformanceBand', 'Performance band')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sites.map((row) => (
                <TableRow key={row.site}>
                  <TableCell>{row.site}</TableCell>
                  <TableCell>{row.region}</TableCell>
                  <TableCell align="right">{applyRoundAndFormat(row.wasteCost, currencyCode)}</TableCell>
                  <TableCell align="right">{row.wasteQuantityKg.toLocaleString()}</TableCell>
                  <TableCell align="right">{row.wastePercentOfSales.toFixed(1)}%</TableCell>
                  <TableCell><PerformanceBandChip band={row.performanceBand} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>

      <Grid container
        spacing={6}>
        <Grid item
          xs={12}
          md={6}>
          <Section>
            <Box sx={{ p: 4 }}>
              <Typography variant="textMD"
                fontWeight={600}
                sx={{ mb: 3 }}>
                {t('wasteDashboardByCategory', 'Waste by product category')}
              </Typography>
              <Box sx={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={CATEGORY_WASTE}
                      dataKey="wasteCost"
                      nameKey="category"
                      outerRadius={90}
                      label={(entry) => `${entry.category}: ${entry.shareOfTotalWastePercent.toFixed(0)}%`}>
                      {CATEGORY_WASTE.map((entry, idx) => (
                        <Cell key={entry.category}
                          fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value: number) => applyRoundAndFormat(value, currencyCode)} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Section>
        </Grid>
        <Grid item
          xs={12}
          md={6}>
          <Section>
            <Box sx={{ p: 4 }}>
              <Typography variant="textMD"
                fontWeight={600}
                sx={{ mb: 3 }}>
                {t('wasteDashboardByMealPeriod', 'Waste by meal period')}
              </Typography>
              <Box sx={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={MEAL_PERIOD_WASTE}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="mealPeriod" />
                    <YAxis />
                    <RechartsTooltip formatter={(value: number) => applyRoundAndFormat(value, currencyCode)} />
                    <Bar dataKey="wasteCost"
                      name={t('wasteDashboardWasteCost', 'Waste cost')}
                      fill={theme.palette.error.main} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Section>
        </Grid>
      </Grid>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardByProduct', 'Waste by product')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('wasteDashboardProduct', 'Product')}</TableCell>
                <TableCell>{t('wasteDashboardCategory', 'Category')}</TableCell>
                <TableCell>{t('wasteDashboardSupplier', 'Supplier')}</TableCell>
                <TableCell align="right">{t('wasteDashboardWasteCost', 'Waste cost')}</TableCell>
                <TableCell align="right">{t('wasteDashboardWeight', 'Weight (kg)')}</TableCell>
                <TableCell align="right">{t('wasteDashboardUnits', 'Units')}</TableCell>
                <TableCell align="right">{t('wasteDashboardOccurrences', 'Occurrences')}</TableCell>
                <TableCell>{t('wasteDashboardPrimaryReason', 'Primary reason')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {PRODUCT_WASTE.map((row) => (
                <TableRow key={row.product}>
                  <TableCell>{row.product}</TableCell>
                  <TableCell>{row.category}</TableCell>
                  <TableCell>{row.supplier}</TableCell>
                  <TableCell align="right">{applyRoundAndFormat(row.wasteCost, currencyCode)}</TableCell>
                  <TableCell align="right">{row.wasteQuantityKg}</TableCell>
                  <TableCell align="right">{row.wasteUnits}</TableCell>
                  <TableCell align="right">{row.occurrences}</TableCell>
                  <TableCell>{row.primaryReason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardBySupplier', 'Waste by supplier')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('wasteDashboardSupplier', 'Supplier')}</TableCell>
                <TableCell align="right">{t('wasteDashboardWasteCost', 'Waste cost')}</TableCell>
                <TableCell align="right">{t('wasteDashboardDamagedDeliveries', 'Damaged deliveries')}</TableCell>
                <TableCell align="right">{t('wasteDashboardRejectedGoods', 'Rejected goods')}</TableCell>
                <TableCell align="right">{t('wasteDashboardQualityScore', 'Quality score')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {SUPPLIER_WASTE.map((row) => (
                <TableRow key={row.supplier}>
                  <TableCell>{row.supplier}</TableCell>
                  <TableCell align="right">{applyRoundAndFormat(row.wasteCost, currencyCode)}</TableCell>
                  <TableCell align="right">{row.damagedDeliveries}</TableCell>
                  <TableCell align="right">{row.rejectedGoods}</TableCell>
                  <TableCell align="right">{row.qualityScore}/100</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardByDayHour', 'Waste by day and time (heatmap of waste cost)')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('wasteDashboardDay', 'Day')}</TableCell>
                {hours.map((hour) => <TableCell key={hour}
                  align="center">{hour}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {days.map((day) => (
                <TableRow key={day}>
                  <TableCell>{day}</TableCell>
                  {hours.map((hour) => {
                    const cell = DAY_HOUR_WASTE.find((c) => c.day === day && c.hour === hour);
                    return (
                      <TableCell key={hour}
                        align="center"
                        sx={{ backgroundColor: cell ? heatCellColor(cell.wasteCost, maxHeat, theme) : undefined }}>
                        {cell ? applyRoundAndFormat(cell.wasteCost, currencyCode, 0) : '-'}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="textXS"
            color="text.secondary"
            sx={{ mt: 2, display: 'block' }}>
            {t('wasteDashboardHeatmapNote', 'Values shown alongside colour so this reads correctly without relying on colour alone.')}
          </Typography>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardByPrepArea', 'Waste by preparation area / department')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('wasteDashboardArea', 'Area')}</TableCell>
                <TableCell align="right">{t('wasteDashboardWasteCost', 'Waste cost')}</TableCell>
                <TableCell align="right">{t('wasteDashboardWeight', 'Weight (kg)')}</TableCell>
                <TableCell>{t('wasteDashboardPrimaryReason', 'Primary reason')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {PREP_AREA_WASTE.map((row) => (
                <TableRow key={row.area}>
                  <TableCell>{row.area}</TableCell>
                  <TableCell align="right">{applyRoundAndFormat(row.wasteCost, currencyCode)}</TableCell>
                  <TableCell align="right">{row.wasteQuantityKg}</TableCell>
                  <TableCell>{row.primaryReason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>
    </Box>
  );
};

export default BreakdownTab;
